import React, { useState, useEffect, useRef } from 'react';
import { getApiUrl } from '../config';
import { useAuth } from '../contexts/AuthContext';
import { useNotification } from './Notification';
import { theme } from '../theme';
import { Notification, NotificationsResponse } from '../types';
import { logger } from '../utils/logger';
import { useResponsive } from '../hooks/useResponsive';
import { formatDateShort, formatNotificationDate } from '../utils/helpers';

interface NotificationsPanelProps {
  isVisible: boolean;
  onClose: () => void;
  onNotificationClick?: (notification: Notification) => void;
}

type FilterType = 'ALL' | 'MATCH_FINISHED' | 'JOIN_APPLICATIONS' | 'REVIEWS' | 'REPORT_VALIDATED';

const NOTIFICATION_FILTERS: { value: FilterType; label: string; icon: string; apiType?: string }[] = [
  { value: 'ALL', label: 'Todas', icon: '📬' },
  { value: 'MATCH_FINISHED', label: 'Partidos', icon: '⚽', apiType: 'MATCH_FINISHED' },
  { value: 'JOIN_APPLICATIONS', label: 'Postulaciones', icon: '🙋', apiType: 'JOIN_APPLICATIONS' },
  { value: 'REVIEWS', label: 'Reseñas', icon: '📝', apiType: 'REVIEW_RECEIVED' },
  { value: 'REPORT_VALIDATED', label: 'Mis stats', icon: '✅', apiType: 'REPORT_VALIDATED' },
];

export const NotificationsPanel: React.FC<NotificationsPanelProps> = ({
  isVisible,
  onClose,
  onNotificationClick
}) => {
  const { user } = useAuth();
  const { isMobile } = useResponsive();
  const { notification, showNotification, hideNotification } = useNotification();
  
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [loading, setLoading] = useState(true);
  const [unreadCount, setUnreadCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [showAll, setShowAll] = useState(false);
  const [hasMoreNotifications, setHasMoreNotifications] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [selectedFilter, setSelectedFilter] = useState<FilterType>('ALL');
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const notificationsContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isVisible && user) {
      // Resetear estado cuando se abre el panel
      setCurrentPage(1);
      setShowAll(false);
      setSelectedFilter('ALL');
      fetchNotifications();
      fetchUnreadCount();
      // Marcar automáticamente como leídas al abrir el panel
      markAllAsRead();
    }
  }, [isVisible, user]);

  useEffect(() => {
    if (isVisible && user && (currentPage > 1 || showAll)) {
      fetchNotifications(currentPage > 1);
    }
  }, [currentPage, showAll]);

  useEffect(() => {
    if (isVisible && user) {
      // Cambio de filtro: reset paginación y refetch
      setCurrentPage(1);
      fetchNotifications();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFilter]);

  const fetchNotifications = async (isLoadMore = false) => {
    try {
      if (isLoadMore) {
        setIsLoadingMore(true);
      } else {
        setLoading(true);
      }

      const token = localStorage.getItem('authToken');

      // Construir URL con parámetros
      const limit = showAll ? 50 : 5;
      const filterConfig = NOTIFICATION_FILTERS.find(f => f.value === selectedFilter);
      const typeParam = filterConfig?.apiType ? `&type=${encodeURIComponent(filterConfig.apiType)}` : '';
      const url = `/api/notifications?page=${currentPage}&limit=${limit}${showAll ? '&showAll=true' : ''}${typeParam}`;
      
      const response = await fetch(getApiUrl(url), {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const data: NotificationsResponse = await response.json();
        
        if (currentPage === 1 && !showAll) {
          // Primera carga: mostrar solo las últimas 5
          setNotifications(data.notifications);
          setHasMoreNotifications(data.pagination.total > 5);
        } else if (showAll) {
          // Modo "Ver todas": mostrar todas las notificaciones
          setNotifications(data.notifications);
          setHasMoreNotifications(false);
        } else {
          // Paginación: agregar a las existentes
          setNotifications(prev => [...prev, ...data.notifications]);
        }
        
        setTotalPages(data.pagination.pages);
      } else {
        const error = await response.json();
        showNotification(error.error || 'Error cargando notificaciones', 'error');
      }
    } catch (error) {
      logger.error('Error fetching notifications:', error);
      showNotification('Error de conexión', 'error');
    } finally {
      setLoading(false);
      setIsLoadingMore(false);
    }
  };

  const fetchUnreadCount = async () => {
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(getApiUrl('/api/notifications/unread-count'), {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setUnreadCount(data.count);
      }
    } catch (error) {
      logger.error('Error fetching unread count:', error);
    }
  };

  const markAsRead = async (notificationId: string) => {
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(getApiUrl(`/api/notifications/${notificationId}/read`), {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        // Actualizar el estado local
        setNotifications(prev => prev.map(n => 
          n.id === notificationId ? { ...n, isRead: true, readAt: new Date().toISOString() } : n
        ));
        setUnreadCount(prev => Math.max(0, prev - 1));
      }
    } catch (error) {
      logger.error('Error marking notification as read:', error);
    }
  };

  const markAllAsRead = async () => {
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(getApiUrl('/api/notifications/mark-all-read'), {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        setNotifications(prev => prev.map(n => ({ ...n, isRead: true, readAt: new Date().toISOString() })));
        setUnreadCount(0);
        // No mostrar toast - el usuario puede ver que las notificaciones se marcaron como leídas visualmente
      }
    } catch (error) {
      logger.error('Error marking all notifications as read:', error);
      showNotification('Error marcando notificaciones como leídas', 'error');
    }
  };

  const handleNotificationClick = (notification: Notification) => {
    if (!notification.isRead) {
      markAsRead(notification.id);
    }
    
    if (onNotificationClick) {
      onNotificationClick(notification);
    }
  };

  const handleShowAll = () => {
    setShowAll(true);
    setCurrentPage(1);
  };

  const handleShowLess = () => {
    setShowAll(false);
    setCurrentPage(1);
  };

  const handleLoadMore = async () => {
    // Guardar la posición actual del scroll
    const container = notificationsContainerRef.current;
    const scrollTop = container?.scrollTop || 0;
    const scrollHeight = container?.scrollHeight || 0;
    
    // Incrementar página y cargar más notificaciones
    setCurrentPage(prev => prev + 1);
    
    // Esperar a que se actualicen las notificaciones
    await fetchNotifications(true);
    
    // Restaurar la posición del scroll después de un pequeño delay
    setTimeout(() => {
      if (container) {
        const newScrollHeight = container.scrollHeight;
        const heightDifference = newScrollHeight - scrollHeight;
        container.scrollTop = scrollTop + heightDifference;
      }
    }, 100);
  };

  const getNotificationIcon = (type: string) => {
    switch (type) {
      case 'MATCH_FINISHED':
        return '⚽';
      case 'REVIEW_RECEIVED':
      case 'REVIEWS_RECEIVED_BATCH':
        return '📝';
      case 'REPORT_VALIDATED':
        return '✅';
      case 'PLAYER_JOINED_MATCH':
      case 'PLAYER_LEFT_MATCH':
        return '👋';
      case 'MATCH_JOIN_APPLICATION_NEW':
        return '🙋';
      case 'MATCH_JOIN_APPLICATION_ACCEPTED':
        return '✅';
      case 'MATCH_JOIN_APPLICATION_REJECTED':
        return '📭';
      case 'MATCH_JOIN_APPLICATION_MESSAGE':
        return '💬';
      case 'GROUP_INVITATION':
        return '🎯';
      case 'GROUP_JOIN_REQUEST':
        return '🙋';
      case 'GROUP_JOIN_ACCEPTED':
        return '✅';
      case 'GROUP_JOIN_REJECTED':
        return '📭';
      case 'GROUP_ANNOUNCEMENT':
        return '📣';
      case 'GROUP_MATCH_CREATED':
        return '🆕';
      case 'GROUP_MATCH_SPOT_OPEN':
        return '🟢';
      case 'GROUP_MEMBER_LEFT':
        return '👋';
      default:
        return 'ℹ️';
    }
  };

  const deleteNotification = async (notificationId: string, event: React.MouseEvent) => {
    event.stopPropagation();
    if (deletingId) return; // evitar dobles clicks
    setDeletingId(notificationId);

    // Optimistic update
    const previous = notifications;
    const wasUnread = notifications.find(n => n.id === notificationId)?.isRead === false;
    setNotifications(prev => prev.filter(n => n.id !== notificationId));
    if (wasUnread) setUnreadCount(prev => Math.max(0, prev - 1));

    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(getApiUrl(`/api/notifications/${notificationId}`), {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) {
        // Rollback
        setNotifications(previous);
        if (wasUnread) setUnreadCount(prev => prev + 1);
        showNotification('No se pudo eliminar la notificación', 'error');
      }
    } catch (error) {
      logger.error('Error deleting notification:', error);
      setNotifications(previous);
      if (wasUnread) setUnreadCount(prev => prev + 1);
      showNotification('Error de conexión', 'error');
    } finally {
      setDeletingId(null);
    }
  };

  const formatDate = (dateString: string) => {
    return formatNotificationDate(dateString);
  };

  if (!isVisible) return null;

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.7)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      padding: isMobile ? '16px' : '32px'
    }}>
      <div style={{
        backgroundColor: theme.colors.surface,
        borderRadius: theme.borderRadius.card,
        padding: isMobile ? '16px' : theme.spacing.lg,
        maxWidth: isMobile ? '100%' : '600px',
        width: '100%',
        maxHeight: '90vh',
        overflow: 'auto',
        boxShadow: theme.colors.shadow,
        border: `2px solid ${theme.colors.primary}`
      }}>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: theme.spacing.lg
        }}>
          <h2 style={{
            margin: 0,
            color: theme.colors.primary,
            fontFamily: theme.fonts.heading,
            fontSize: isMobile ? '20px' : theme.fonts.sizeTitle
          }}>
            Notificaciones {unreadCount > 0 && `(${unreadCount} nuevas)`}
          </h2>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              fontSize: '24px',
              cursor: 'pointer',
              color: theme.colors.textSecondary
            }}
          >
            ×
          </button>
        </div>

        {/* Filter chips */}
        <div style={{
          display: 'flex',
          gap: theme.spacing.xs,
          marginBottom: theme.spacing.md,
          overflowX: 'auto',
          paddingBottom: '4px',
          WebkitOverflowScrolling: 'touch'
        }}>
          {NOTIFICATION_FILTERS.map((filter) => {
            const isSelected = selectedFilter === filter.value;
            return (
              <button
                key={filter.value}
                type="button"
                onClick={() => setSelectedFilter(filter.value)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                  padding: `${theme.spacing.xs} ${theme.spacing.sm}`,
                  backgroundColor: isSelected ? theme.colors.primary : 'transparent',
                  color: isSelected ? theme.colors.white : theme.colors.text,
                  border: `1px solid ${isSelected ? theme.colors.primary : theme.colors.border}`,
                  borderRadius: '20px',
                  fontFamily: theme.fonts.body,
                  fontSize: theme.fonts.sizeSmall,
                  cursor: 'pointer',
                  whiteSpace: 'nowrap',
                  transition: 'all 0.15s ease'
                }}
              >
                <span style={{ fontSize: '14px' }}>{filter.icon}</span>
                <span>{filter.label}</span>
              </button>
            );
          })}
        </div>

        {/* Botones de control */}
        <div style={{
          marginBottom: theme.spacing.md,
          display: 'flex',
          gap: theme.spacing.sm,
          flexWrap: 'wrap',
          alignItems: 'center'
        }}>
          {unreadCount > 0 && (
            <button
              onClick={markAllAsRead}
              style={{
                padding: `${theme.spacing.sm} ${theme.spacing.md}`,
                backgroundColor: theme.colors.primary,
                color: theme.colors.white,
                border: 'none',
                borderRadius: theme.borderRadius.button,
                fontFamily: theme.fonts.body,
                fontSize: theme.fonts.sizeSmall,
                cursor: 'pointer'
              }}
            >
              Marcar todas como leídas
            </button>
          )}
          
          {!showAll && hasMoreNotifications && (
            <button
              onClick={handleShowAll}
              style={{
                padding: `${theme.spacing.sm} ${theme.spacing.md}`,
                backgroundColor: theme.colors.secondary,
                color: theme.colors.white,
                border: 'none',
                borderRadius: theme.borderRadius.button,
                fontFamily: theme.fonts.body,
                fontSize: theme.fonts.sizeSmall,
                cursor: 'pointer'
              }}
            >
              Ver todas las notificaciones
            </button>
          )}
          
          {showAll && (
            <button
              onClick={handleShowLess}
              style={{
                padding: `${theme.spacing.sm} ${theme.spacing.md}`,
                backgroundColor: theme.colors.textSecondary,
                color: theme.colors.white,
                border: 'none',
                borderRadius: theme.borderRadius.button,
                fontFamily: theme.fonts.body,
                fontSize: theme.fonts.sizeSmall,
                cursor: 'pointer'
              }}
            >
              Ver solo las últimas 5
            </button>
          )}
        </div>

        {loading ? (
          <div style={{ textAlign: 'center', padding: theme.spacing.lg }}>
            Cargando notificaciones...
          </div>
        ) : notifications.length === 0 ? (
          <div style={{
            textAlign: 'center',
            padding: theme.spacing.lg,
            color: theme.colors.textSecondary,
            fontFamily: theme.fonts.body
          }}>
            {selectedFilter === 'ALL'
              ? 'No tienes notificaciones'
              : `No tienes notificaciones de "${NOTIFICATION_FILTERS.find(f => f.value === selectedFilter)?.label}"`}
          </div>
        ) : (
          <div ref={notificationsContainerRef} style={{ maxHeight: '60vh', overflowY: 'auto' }}>
            {notifications.map((notification) => (
              <div
                key={notification.id}
                role="button"
                tabIndex={0}
                onClick={() => handleNotificationClick(notification)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    handleNotificationClick(notification);
                  }
                }}
                style={{
                  position: 'relative',
                  padding: theme.spacing.md,
                  paddingRight: '40px',
                  marginBottom: theme.spacing.sm,
                  backgroundColor: notification.isRead ? theme.colors.background : theme.colors.primary + '20',
                  borderRadius: theme.borderRadius.card,
                  border: `1px solid ${notification.isRead ? theme.colors.border : theme.colors.primary}`,
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  opacity: notification.isRead ? 0.8 : 1,
                  width: '100%',
                  textAlign: 'left'
                }}
              >
                <button
                  type="button"
                  aria-label="Eliminar notificación"
                  onClick={(e) => deleteNotification(notification.id, e)}
                  disabled={deletingId === notification.id}
                  style={{
                    position: 'absolute',
                    top: '8px',
                    right: '8px',
                    width: '28px',
                    height: '28px',
                    padding: 0,
                    background: 'transparent',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: deletingId === notification.id ? 'wait' : 'pointer',
                    color: theme.colors.textSecondary,
                    fontSize: '14px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    opacity: deletingId === notification.id ? 0.4 : 0.7,
                    transition: 'opacity 0.15s ease, background-color 0.15s ease'
                  }}
                  onMouseEnter={(e) => {
                    if (deletingId !== notification.id) {
                      e.currentTarget.style.opacity = '1';
                      e.currentTarget.style.backgroundColor = theme.colors.danger + '20';
                    }
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.opacity = '0.7';
                    e.currentTarget.style.backgroundColor = 'transparent';
                  }}
                >
                  🗑
                </button>
                <div style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: theme.spacing.sm
                }}>
                  <div style={{
                    fontSize: '20px',
                    marginTop: '2px'
                  }}>
                    {getNotificationIcon(notification.type)}
                  </div>
                  <div style={{ flex: 1 }}>
                    <h4 style={{
                      margin: '0 0 4px 0',
                      color: theme.colors.text,
                      fontFamily: theme.fonts.body,
                      fontSize: theme.fonts.sizeBody,
                      fontWeight: theme.fonts.weightBold
                    }}>
                      {notification.title}
                    </h4>
                    <p style={{
                      margin: '0 0 8px 0',
                      color: theme.colors.textSecondary,
                      fontFamily: theme.fonts.body,
                      fontSize: theme.fonts.sizeSmall,
                      lineHeight: '1.4'
                    }}>
                      {notification.message}
                    </p>
                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center'
                    }}>
                      <span style={{
                        color: theme.colors.textSecondary,
                        fontFamily: theme.fonts.body,
                        fontSize: theme.fonts.sizeSmall
                      }}>
                        {formatDate(notification.createdAt)}
                      </span>
                      {!notification.isRead && (
                        <span style={{
                          backgroundColor: theme.colors.primary,
                          color: theme.colors.white,
                          padding: '2px 6px',
                          borderRadius: '10px',
                          fontSize: '10px',
                          fontFamily: theme.fonts.body,
                          fontWeight: theme.fonts.weightBold
                        }}>
                          NUEVA
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}

            {/* Controles de navegación */}
            {!showAll && hasMoreNotifications && (
              <div style={{
                display: 'flex',
                justifyContent: 'center',
                marginTop: theme.spacing.lg
              }}>
                <button
                  onClick={handleLoadMore}
                  disabled={isLoadingMore}
                  style={{
                    padding: `${theme.spacing.sm} ${theme.spacing.lg}`,
                    backgroundColor: isLoadingMore ? theme.colors.border : theme.colors.primary,
                    color: isLoadingMore ? theme.colors.textSecondary : theme.colors.white,
                    border: 'none',
                    borderRadius: theme.borderRadius.button,
                    fontFamily: theme.fonts.body,
                    fontSize: theme.fonts.sizeSmall,
                    cursor: isLoadingMore ? 'not-allowed' : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: theme.spacing.sm,
                    opacity: isLoadingMore ? 0.6 : 1
                  }}
                >
                  {isLoadingMore ? 'Cargando...' : 'Cargar más notificaciones'}
                  <span>{isLoadingMore ? '⏳' : '📄'}</span>
                </button>
              </div>
            )}
            
            {showAll && totalPages > 1 && (
              <div style={{
                display: 'flex',
                justifyContent: 'center',
                gap: theme.spacing.sm,
                marginTop: theme.spacing.lg
              }}>
                <button
                  onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                  disabled={currentPage === 1}
                  style={{
                    padding: `${theme.spacing.sm} ${theme.spacing.md}`,
                    backgroundColor: currentPage === 1 ? theme.colors.border : theme.colors.primary,
                    color: currentPage === 1 ? theme.colors.textSecondary : theme.colors.white,
                    border: 'none',
                    borderRadius: theme.borderRadius.button,
                    fontFamily: theme.fonts.body,
                    fontSize: theme.fonts.sizeSmall,
                    cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                    opacity: currentPage === 1 ? 0.6 : 1
                  }}
                >
                  Anterior
                </button>
                <span style={{
                  padding: `${theme.spacing.sm} ${theme.spacing.md}`,
                  color: theme.colors.text,
                  fontFamily: theme.fonts.body,
                  fontSize: theme.fonts.sizeSmall
                }}>
                  {currentPage} de {totalPages}
                </span>
                <button
                  onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                  disabled={currentPage === totalPages}
                  style={{
                    padding: `${theme.spacing.sm} ${theme.spacing.md}`,
                    backgroundColor: currentPage === totalPages ? theme.colors.border : theme.colors.primary,
                    color: currentPage === totalPages ? theme.colors.textSecondary : theme.colors.white,
                    border: 'none',
                    borderRadius: theme.borderRadius.button,
                    fontFamily: theme.fonts.body,
                    fontSize: theme.fonts.sizeSmall,
                    cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
                    opacity: currentPage === totalPages ? 0.6 : 1
                  }}
                >
                  Siguiente
                </button>
              </div>
            )}
          </div>
        )}

        {/* Notificación */}
        {notification.isVisible && (
          <button type="button" onClick={hideNotification} style={{
            position: 'fixed',
            top: '20px',
            right: '20px',
            padding: '16px 20px',
            borderRadius: '8px',
            color: 'white',
            fontWeight: 'bold',
            fontSize: '14px',
            zIndex: 1001,
            maxWidth: '400px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            cursor: 'pointer',
            border: 'none',
            backgroundColor: notification.type === 'success' ? theme.colors.success :
                           notification.type === 'error' ? theme.colors.danger :
                           notification.type === 'warning' ? theme.colors.warning : theme.colors.primary
          }}>
            <span>{notification.type === 'success' ? '✅' : notification.type === 'error' ? '❌' : notification.type === 'warning' ? '⚠️' : 'ℹ️'}</span>
            <span>{notification.message}</span>
          </button>
        )}
      </div>
    </div>
  );
}; 